import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutGrid, X } from "lucide-react";
import { links, roleLinks } from "./navLinks";

function Tab({ to, label, icon: Icon, end, tid }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={`${tid}-mobile`}
      className={({ isActive }) =>
        `relative flex flex-1 flex-col items-center gap-1 py-2 text-[10px] font-medium transition-colors duration-150 ${
          isActive ? "text-cyan-300" : "text-slate-400 active:text-slate-200"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span className={`absolute top-0 h-0.5 w-8 rounded-full bg-cyan-400 transition-opacity duration-200 ${isActive ? "opacity-100" : "opacity-0"}`} />
          <Icon size={20} strokeWidth={isActive ? 2.4 : 1.8} />
          {label}
        </>
      )}
    </NavLink>
  );
}

function MoreSheet({ open, onClose, user }) {
  const extra = [...links.filter((l) => !l.primary), ...(user ? roleLinks[user.role] || [] : [])];
  return (
    <>
      <div
        onClick={onClose}
        data-testid="nav-more-backdrop"
        className={`fixed inset-0 z-40 bg-black/60 backdrop-blur-sm transition-opacity duration-200 lg:hidden ${open ? "opacity-100" : "pointer-events-none opacity-0"}`}
      />
      <div
        data-testid="nav-more-sheet"
        className={`fixed inset-x-0 bottom-0 z-50 rounded-t-2xl border-t border-white/10 bg-[#0b0f14] pb-[calc(env(safe-area-inset-bottom)+5rem)] shadow-2xl transition-transform duration-300 ease-out lg:hidden ${open ? "translate-y-0" : "translate-y-full"}`}
      >
        <div className="flex items-center justify-between px-5 pt-4">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">More tools</div>
          <button onClick={onClose} data-testid="nav-more-close" className="rounded-full border border-white/10 p-1.5 text-slate-300"><X size={14} /></button>
        </div>
        <div className="grid grid-cols-4 gap-2 px-4 pt-3">
          {extra.map(({ to, label, icon: Icon, tid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`${tid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1.5 rounded-xl border px-1 py-3 text-[10px] font-medium ${
                  isActive ? "border-cyan-400/50 bg-cyan-500/10 text-cyan-200" : "border-white/10 bg-white/[0.03] text-slate-300 active:bg-white/10"
                }`
              }
            >
              <Icon size={18} />
              {label}
            </NavLink>
          ))}
        </div>
      </div>
    </>
  );
}

export default function BottomTabBar({ user }) {
  const [more, setMore] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setMore(false), [pathname]);
  const primary = links.filter((l) => l.primary);
  const moreActive = !primary.some((l) => (l.end ? pathname === l.to : pathname.startsWith(l.to)));

  return (
    <>
      <MoreSheet open={more} onClose={() => setMore(false)} user={user} />
      <nav
        data-testid="bottom-tab-bar"
        className="fixed inset-x-0 bottom-0 z-50 flex border-t border-white/10 bg-black/85 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl lg:hidden"
      >
        {primary.map((l) => <Tab key={l.to} {...l} />)}
        <button
          onClick={() => setMore((v) => !v)}
          data-testid="nav-more-mobile"
          aria-expanded={more}
          className={`relative flex flex-1 flex-col items-center gap-1 py-2 text-[10px] font-medium ${more || moreActive ? "text-cyan-300" : "text-slate-400"}`}
        >
          <span className={`absolute top-0 h-0.5 w-8 rounded-full bg-cyan-400 transition-opacity duration-200 ${moreActive && !more ? "opacity-100" : "opacity-0"}`} />
          <LayoutGrid size={20} strokeWidth={more ? 2.4 : 1.8} />
          More
        </button>
      </nav>
    </>
  );
}
